import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { ApiCall } from "components/common/services/ApiServices";
import CustomNotify from "components/common/atoms/CustomNotify";
import { t } from "../translation/Translation";
import { selectAuth } from "features/auth/AuthSlice";
import {
  CLARITALK_GET_SOUND_SETTINGS,
  CLARITALK_SAVE_SOUND_SETTINGS,
} from "routes/ApiEndpoints";
import {
  CENTRAL_DATA_MANAGEMENT_MICROSERVICE,
  SUCCESS,
  ERROR,
} from "Constants";
import Title from "components/common/atoms/Title";
import Button from "components/common/atoms/Button";
import SelectWithSearch from "components/common/atoms/SelectWithSearch";
import SpinnerWrapper from "components/common/atoms/SpinnerWrapper";
import LoadingIcon from "components/common/utlis/LoadingIcon";
import "./claritalk.css";

interface AudioDevice {
  Id: string;
  Name: string;
}

interface SoundSettingsData {
  selectedMicrophone: AudioDevice | null;
  selectedSpeaker: AudioDevice | null;
  microphones: AudioDevice[];
  speakers: AudioDevice[];
  hasStoredSettings: boolean;
}

const ClaritalkSoundSettings: React.FC = () => {
  const [settings, setSettings] = useState<SoundSettingsData>({
    selectedMicrophone: null,
    selectedSpeaker: null,
    microphones: [],
    speakers: [],
    hasStoredSettings: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({
    microphone: "",
    speaker: "",
  });

  const authUser = useSelector(selectAuth);
  const navigate = useNavigate();

  // Fetch sound settings on component mount
  useEffect(() => {
    fetchSoundSettings();
  }, []);

  const fetchSoundSettings = async () => {
    setLoading(true);
    try {
      const response = await ApiCall.service(
        CLARITALK_GET_SOUND_SETTINGS,
        "POST",
        { userId: authUser.userId },
        true,
        CENTRAL_DATA_MANAGEMENT_MICROSERVICE,
      );

      if (response?.status === 200) {
        const data = response.data;
        setSettings({
          selectedMicrophone: data.selectedMicrophone || null,
          selectedSpeaker: data.selectedSpeaker || null,
          microphones: data.microphones || [],
          speakers: data.speakers || [],
          hasStoredSettings: data.hasStoredSettings || false,
        });
      } else {
        CustomNotify({
          type: ERROR,
          message: t(response?.error || "Failed to load sound settings"),
        });
      }
    } catch (error) {
      console.error("Failed to fetch sound settings:", error);
      CustomNotify({
        type: ERROR,
        message: t(
          "Failed to load sound settings. Please ensure Claritalk is running.",
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMicrophoneChange = (selected: any) => {
    const selectedDevice =
      settings.microphones.find((m) => m.Id === selected?.value) || null;
    setSettings((prev) => ({
      ...prev,
      selectedMicrophone: selectedDevice,
    }));
    setErrors((prev) => ({ ...prev, microphone: "" }));
  };

  const handleSpeakerChange = (selected: any) => {
    const selectedDevice =
      settings.speakers.find((s) => s.Id === selected?.value) || null;
    setSettings((prev) => ({
      ...prev,
      selectedSpeaker: selectedDevice,
    }));
    setErrors((prev) => ({ ...prev, speaker: "" }));
  };

  const validateForm = (): boolean => {
    const newErrors = {
      microphone: "",
      speaker: "",
    };

    if (!settings.selectedMicrophone?.Id) {
      newErrors.microphone = t("Please select a microphone");
    }

    if (!settings.selectedSpeaker?.Id) {
      newErrors.speaker = t("Please select a speaker");
    }

    setErrors(newErrors);
    return !newErrors.microphone && !newErrors.speaker;
  };

  const handleSaveSettings = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const response = await ApiCall.service(
        CLARITALK_SAVE_SOUND_SETTINGS,
        "POST",
        {
          userId: authUser.userId,
          microphoneId: settings.selectedMicrophone?.Id,
          microphoneName: settings.selectedMicrophone?.Name,
          speakerId: settings.selectedSpeaker?.Id,
          speakerName: settings.selectedSpeaker?.Name,
        },
        true,
        CENTRAL_DATA_MANAGEMENT_MICROSERVICE,
      );

      if (response?.status === 200) {
        CustomNotify({
          type: SUCCESS,
          message: t("Sound settings saved successfully"),
        });
        setSettings((prev) => ({
          ...prev,
          hasStoredSettings: true,
        }));
      } else {
        CustomNotify({
          type: ERROR,
          message: t(response?.error || "Failed to save sound settings"),
        });
      }
    } catch (error) {
      console.error("Failed to save sound settings:", error);
      CustomNotify({
        type: ERROR,
        message: t("Failed to save sound settings"),
      });
    } finally {
      setSaving(false);
    }
  };

  // const handleRefresh = () => {
  //   fetchSoundSettings();
  // };

  const handleBack = () => {
    navigate(-1);
  };

  const microphoneOptions = settings.microphones.map((mic) => ({
    value: mic.Id,
    label: mic.Name,
  }));

  const speakerOptions = settings.speakers.map((speaker) => ({
    value: speaker.Id,
    label: speaker.Name,
  }));


  return (
    <>
      <Title title={t("Claritalk sound settings")} />
      {loading ? (
        <div className="row">
          <div className="col-12">
            <SpinnerWrapper
              isDiv={true}
              message={t("Loading sound settings...")}
              className="py-5"
            />
          </div>
        </div>
      ) : (
        <>
          <div className="row">
            <div className="col-12">
              <div className="form-border">
                <p className="mb-4 text-muted">
                  {t(
                    "Configure your microphone and speaker settings for Claritalk recordings. These settings will be used automatically when you start a recording.",
                  )}
                </p>

                <div className="row">
                  {/* Microphone Selection */}
                  <div className="col-sm-12 col-md-6">
                    <SelectWithSearch
                      title={t("Microphone")}
                      name="microphone"
                      search={true}
                      options={microphoneOptions}
                      placeHolder={t("Select a microphone")}
                      value={
                        settings.selectedMicrophone
                          ? { value: settings.selectedMicrophone.Id, label: settings.selectedMicrophone.Name }
                          : null
                      }
                      onChange={handleMicrophoneChange}
                      isMandatory={true}
                      error={errors.microphone}
                      className="rounded-3"
                    />
                    {settings.selectedMicrophone && (
                      <div className="claritalk-current-device mb-3">
                        <i className="pi pi-check-circle text-success me-2"></i>
                        <span>
                          {t("Currently selected")}:{" "}
                          {settings.selectedMicrophone.Name}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Speaker Selection */}
                  <div className="col-sm-12 col-md-6">
                    <SelectWithSearch
                      title={t("Speaker")}
                      name="speaker"
                      search={true}
                      options={speakerOptions}
                      placeHolder={t("Select a speaker")}
                      value={
                        settings.selectedSpeaker
                          ? { value: settings.selectedSpeaker.Id, label: settings.selectedSpeaker.Name }
                          : null
                      }
                      onChange={handleSpeakerChange}
                      isMandatory={true}
                      error={errors.speaker}
                      className="rounded-3"
                    />
                    {settings.selectedSpeaker && (
                      <div className="claritalk-current-device mb-3">
                        <i className="pi pi-check-circle text-success me-2"></i>
                        <span>
                          {t("Currently selected")}:{" "}
                          {settings.selectedSpeaker.Name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Status Indicator */}
                {settings.hasStoredSettings && (
                  <div className="claritalk-settings-status mb-3">
                    <i className="pi pi-info-circle me-2"></i>
                    <span>
                      {t(
                        "Your settings are saved and will be used automatically for recordings.",
                      )}
                    </span>
                  </div>
                )}

                {/* No Devices Warning */}
                {(settings.microphones.length === 0 ||
                  settings.speakers.length === 0) && (
                  <div className="claritalk-settings-warning mb-3">
                    <i className="pi pi-exclamation-triangle me-2"></i>
                    <span>
                      {t(
                        "No audio devices detected. Please ensure Claritalk is running and your devices are connected.",
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="row backPadding">
            <div className="col-6 align-self-center">
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleBack();
                }}
                className="back-btn text-decoration-underline"
              >
                {t("Back")}
              </Link>
            </div>
            <div className="col-6 text-end">
              {/* <Button
                title={t("Refresh Devices")}
                handleClick={handleRefresh}
                className="form-button shadow-none me-3"
                style={{ backgroundColor: "#6c757d", borderColor: "#6c757d" }}
                disabled={loading}
              /> */}
              {!saving ? (
                <Button
                  title={t("Save Settings")}
                  handleClick={handleSaveSettings}
                  className="form-button shadow-none"
                  disabled={settings.microphones.length === 0}
                />
              ) : (
                <LoadingIcon
                  iconType="bars"
                  color="#ff4dae"
                  className="float-end"
                />
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ClaritalkSoundSettings;
